import { createContext, useContext, useEffect, useMemo } from 'react'
import { useOrganizationSettings } from '../api/hooks'
import { applyTheme, DEFAULT_BRAND, resolveBrand, type ResolvedBrand } from './applyTheme'

/**
 * Applies the active organization's white-label theme to the document.
 *
 * WHERE THIS SITS AND WHY
 *
 * Above the organization gate, not below it. The gate is a screen like any other — it
 * carries the lockup and the product's name — and for the great majority of users, who
 * belong to exactly one organization, that organization is already resolved by the time
 * the gate renders. Mounting below the gate would leave those users looking at Tracer's
 * navy for the one frame before their own brand arrived.
 *
 * A user who belongs to several organizations and has not chosen one is the case where
 * there is genuinely no answer yet, and they see the product's own brand until they
 * pick. That is correct: we do not know whose product this is until they say.
 *
 * NO FLASH, BUT ALSO NO BLOCKING
 *
 * The settings request is not awaited before children render. Blocking the whole
 * application on a cosmetic fetch would mean an API that is merely slow — or an endpoint
 * that 500s — turns into a white screen instead of an unbranded one. The theme lands a
 * few hundred milliseconds in and the page restyles; every colour on it comes from a
 * custom property, so the restyle is one repaint and touches no layout that is not
 * already themed.
 */

const BrandContext = createContext<ResolvedBrand>(DEFAULT_BRAND)

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { data: settings } = useOrganizationSettings()

  /*
      Identity is taken from the serialized document, not from the object.

      React Query hands back a fresh object on every refetch even when the bytes are
      identical, and refetches happen on a timer. Keyed on the object, every refetch
      would rip every custom property off the document and write it back — a full
      restyle, several times a minute, for no change at all. Keyed on the bytes, nothing
      moves until something actually changes.
  */
  const fingerprint = JSON.stringify(settings?.theme ?? null)
  const theme = useMemo(() => settings?.theme ?? null, [fingerprint]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  const brand = useMemo(() => resolveBrand(theme), [theme])

  return <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>
}

/**
 * The product's name, tagline and mark, as this organization sees them.
 *
 * Always complete: a theme that sets only a logo still resolves to a full brand, so no
 * caller has to render "themed, but with no name".
 */
export function useBrand(): ResolvedBrand {
  return useContext(BrandContext)
}
